import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../api";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { FaBuilding, FaCalendarAlt, FaCreditCard, FaFileAlt } from 'react-icons/fa';
import { IoArrowBackOutline } from 'react-icons/io5';
 
// Theme-aware card style
const cardStyle = {
  background: 'var(--panel)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow-sm)',
  padding: '24px',
  border: '1px solid var(--border)'
};
 
// Main container style
const mainContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  maxWidth: '1200px',
  margin: '40px auto',
  padding: '0 20px',
  color: 'var(--text)'
};
 
// Column wrapper style for the two main columns
const columnWrapperStyle = {
  display: 'flex',
  gap: '24px',
  alignItems: 'flex-start',
  width: '100%',
};
 
function ProceedToPayment() {
  const { expenseId } = useParams();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailSent, setEmailSent] = useState(false);
  const [sentToPayment, setSentToPayment] = useState(false);
  const [showSentBanner, setShowSentBanner] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState('');
  const [qrFile, setQrFile] = useState(null);
 
  const [sending, setSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const { instance, accounts } = useMsal();

  useEffect(() => {
    const fetchSelectedVendor = async () => {
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ["User.Read"],
          account: accounts[0],
        });
        // Double-encode to preserve IDs that contain '/'
        const encodedId = encodeURIComponent(encodeURIComponent(expenseId));
        const res = await api.get(
          `/api/payments/selected-vendor/${encodedId}`,
          { headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } }
        );
        setVendor(res.data);
        setSentToPayment(res.data.sent_to_payment);
        setEmailSent(res.data.sent_to_payment);
        setShowSentBanner(res.data.sent_to_payment);

      } catch (err) {
        console.error("Failed to fetch selected vendor", err);
        setVendor(null);
      } finally {
        setLoading(false);
      }
    };
    fetchSelectedVendor();
  }, [expenseId]);

  const handleSendEmail = async () => {

    setSending(true);
    setShowError(false);
    setShowSentBanner(false);
    try {
      const account = accounts[0];
      const result = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });

      const accessToken = result.accessToken;
      const authToken = await instance.acquireTokenSilent({
        scopes: ["User.Read"],
        account: account,
      });

      // Prepare form data
      const formData = new FormData();
      formData.append("vendor", JSON.stringify(vendor));
      formData.append("expenseId", expenseId);
      formData.append("accessToken", accessToken);
      formData.append("paymentDetails", paymentDetails);
      if (qrFile) {
        formData.append("qrFile", qrFile);
      }

      await api.post("/api/payments/send-payment-email", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken.accessToken}`,
        }
      });

      setEmailSent(true);
      setSentToPayment(true);
      setShowSuccess(true);
    } catch (err) {
      setShowError(true);
      console.error("Failed to send email", err);
    } finally {
      setSending(false);
    }
  };
 
  const handleSavePaymentDetails = () => {
    // TODO: Implement backend API call to save payment details and QR code
    alert('Payment details and QR code saved (demo only)');
  };
 
  useEffect(() => {
    if (showSuccess || showError) {
      const timer = setTimeout(() => {
        setShowSuccess(false);
        setShowError(false);
        if (showSuccess) setShowSentBanner(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess, showError]);
 
  const formatDate = (isoDate) => {
    if (!isoDate) return 'N/A';
    const date = new Date(isoDate);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };
 
  if (loading) return <div style={mainContainerStyle}>Loading...</div>;
  if (!vendor) return <div style={mainContainerStyle}>No selected vendor found for this expense.</div>;
 
  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', padding: '12px 0', color: 'var(--text)' }}>
      <div style={mainContainerStyle}>
 
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {/* Header Section */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>

            <div style={{ color: 'var(--text)', fontSize: '18px', fontWeight: '500' }}>
              {expenseId}
            </div>
          </div>
 
          {/* Page Title and Subtitle */}
          <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '24px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--text)', margin: '0' }}>
              Payment Processing
            </h1>
            <p style={{ fontSize: '16px', color: 'var(--muted)', margin: '8px 0 0' }}>
              Review and submit vendor payment request
            </p>
          </div>
 
          {/* Main Content Area with two columns */}
          <div style={columnWrapperStyle}>

            {/* Left Column (Vendor Details) */}
            <div style={{ flex: '2', display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={cardStyle}>
                {/* Vendor Details Header */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <FaFileAlt style={{ color: 'var(--accent)', fontSize: '24px', marginRight: '12px' }} />
                  <div>
                    <h4 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', margin: '0' }}>
                      Vendor Details
                    </h4>
                    <p style={{ fontSize: '14px', color: 'var(--muted)', margin: '4px 0 0' }}>
                      Payment information and terms
                    </p>
                  </div>
                </div>

                {/* Vendor Name */}
                <div style={{ ...cardStyle, padding: '16px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Vendor Name</div>
                  <div style={{ display: 'flex', alignItems: 'center', fontSize: '16px', color: 'var(--text)' }}>
                    <FaBuilding style={{ marginRight: '8px', color: 'var(--muted)' }} />
                    <strong>{vendor.vendor_name}</strong>
                  </div>
                </div>

                {/* Total Amount */}
                <div style={{ ...cardStyle, padding: '24px', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.35)' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#047857' }}>
                    Total Amount
                  </div>
                  <div style={{ fontSize: '40px', fontWeight: 'bold', color: '#047857', marginTop: '8px' }}>
                    ₹{vendor.actual_paid.toLocaleString('en-US')}
                  </div>
                </div>

                {/* Date and Payment Terms */}
                <div style={{ display: 'flex', gap: '24px', marginTop: '24px' }}>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <FaCalendarAlt style={{ color: 'var(--muted)', fontSize: '20px', marginRight: '12px' }} />
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Date</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: 'var(--text)', marginTop: '4px' }}>
                        {formatDate(vendor.date)}
                      </div>
                    </div>
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <FaCreditCard style={{ color: 'var(--muted)', fontSize: '20px', marginRight: '12px' }} />
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Payment Terms</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: 'var(--text)', marginTop: '4px' }}>
                        {vendor.payment_terms}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '24px' }}>
                  <FaFileAlt style={{ color: 'var(--muted)', fontSize: '20px', marginRight: '12px' }} />
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Description</div>
                    <div style={{ fontSize: '16px', fontWeight: '500', color: 'var(--text)', marginTop: '4px' }}>
                      {vendor.description}
                    </div>
                  </div>
                </div>
              </div>

              <Link to="/expenses" style={{ color: 'var(--accent)', textDecoration: 'none', display: 'flex', alignItems: 'center', fontSize: '18px', fontWeight: '500' }}>
                <IoArrowBackOutline style={{ marginRight: '8px' }} />
                Back to Expenses
              </Link>
            </div>

            {/* Right Column (Payment Action & Status) */}
            <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '24px' }}>

              {/* Payment Action Card */}
              <div style={cardStyle}>
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', margin: '0' }}>
                  Payment Action
                </h4>
                <p style={{ fontSize: '14px', color: 'var(--muted)', margin: '4px 0 24px' }}>
                  Submit this request to the payments team
                </p>

                {!sentToPayment ? (
                  <button
                    onClick={handleSendEmail}
                    disabled={sending}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      padding: '12px 20px',
                      borderRadius: '8px',
                      background: 'var(--primary)',
                      color: '#fff',
                      border: '1px solid var(--primary-700)',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      opacity: sending ? 0.7 : 1,
                    }}
                  >
                    {sending ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: '8px' }}></span>
                        {" Sending..."}
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                          <line x1="22" y1="2" x2="11" y2="13"></line>
                          <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                        </svg>
                        Send to Payments Team
                      </>
                    )}
                  </button>
                ) : (
                  showSentBanner && (
                    <div style={{ padding: '16px', background: 'rgba(34,197,94,0.12)', color: '#065F46', borderRadius: '8px', fontSize: '14px', fontWeight: '500', textAlign: 'center', border: '1px solid rgba(34,197,94,0.35)' }}>
                      Email already sent to payments team!
                    </div>
                  )
                )}
              </div>

              {/* Request Status Card */}
              <div style={cardStyle}>
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', margin: '0 0 16px' }}>
                  Request Status
                </h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', color: 'var(--muted)' }}>Status</span>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#703d19ff', padding: '4px 12px', background: 'rgba(245, 158, 11, 0.15)', borderRadius: '9999px', border: '1px solid rgba(245, 158, 11, 0.35)' }}>
                    {sentToPayment ? 'Sent' : 'Pending'}
                  </span>
                </div>
              </div>

              {/* Payment Details & QR Upload (no save button) */}
              <div style={cardStyle}>
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', margin: '0 0 16px' }}>
                  Payment Details
                </h4>

                {/* Payment details textarea */}
                <div style={{ marginBottom: '16px' }}>
                  <textarea
                    id="paymentDetails"
                    value={paymentDetails}
                    onChange={e => setPaymentDetails(e.target.value)}
                    rows={3}
                    style={{
                      width: '100%',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      padding: '10px',
                      fontSize: '15px',
                      color: 'var(--text)',
                      background: 'var(--panel)',
                      resize: 'vertical'
                    }}
                    placeholder="Enter UPI ID or Bank Account No"
                  />
                </div>

                {/* QR Code upload */}
                <div>
                  <label
                    htmlFor="qrCode"
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      color: 'var(--muted)',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}
                  >
                    QR Code (JPG or PDF)
                  </label>

                  <div
                    style={{
                      border: '2px dashed var(--border)',
                      borderRadius: '8px',
                      padding: qrFile ? '8px' : '20px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      background: 'var(--panel)',
                      position: 'relative',
                      transition: 'border-color 0.2s',
                      minHeight: '120px'
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = getComputedStyle(document.documentElement).getPropertyValue('--accent') || '#6366f1')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = getComputedStyle(document.documentElement).getPropertyValue('--border') || '#cbd5e1')}
                  >
                    <input
                      id="qrCode"
                      type="file"
                      accept=".jpg,.jpeg,.pdf"
                      onChange={e => setQrFile(e.target.files[0])}
                      style={{ display: 'none' }}
                    />

                    {!qrFile ? (
                      <>
                        <label htmlFor="qrCode" style={{ cursor: 'pointer', color: 'var(--accent)', fontWeight: '500' }}>
                          Click to upload or drag & drop
                        </label>
                        <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                          JPG or PDF up to 5MB
                        </p>
                      </>
                    ) : (
                      <>
                        {qrFile.type.startsWith('image/') ? (
                          <img
                            src={URL.createObjectURL(qrFile)}
                            alt="QR Preview"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '150px',
                              borderRadius: '8px',
                              objectFit: 'contain'
                            }}
                          />
                        ) : (
                          <p style={{ fontSize: '13px', color: 'var(--text)' }}>{qrFile.name}</p>
                        )}

                        {/* Remove icon button */}
                        <button
                          type="button"
                          onClick={() => setQrFile(null)}
                          style={{
                            position: 'absolute',
                            top: '6px',
                            right: '6px',
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '50%',
                            width: '22px',
                            height: '22px',
                            fontSize: '14px',
                            lineHeight: '20px',
                            cursor: 'pointer'
                          }}
                        >
                          ×
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
 
      {/* Success popup */}
      {showSuccess && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            minWidth: 300,
            textAlign: "center",
            padding: '16px 24px',
            backgroundColor: '#D1FAE5',
            color: '#065F46',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            fontSize: '16px',
            fontWeight: '600'
          }}
        >
          Email sent successfully!
        </div>
      )}
      {/* Error popup */}
      {showError && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            minWidth: 300,
            textAlign: "center",
            padding: '16px 24px',
            backgroundColor: '#FEE2E2',
            color: '#991B1B',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            fontSize: '16px',
            fontWeight: '600'
          }}
        >
          Failed to send email. Please try again.
        </div>
      )}
    </div>
  );
}
 
export default ProceedToPayment;





// import { useParams } from "react-router-dom";
// import { useEffect, useState } from "react";
// import axios from "axios";
// import { useMsal } from "@azure/msal-react";
// import { loginRequest } from "../authConfig";
 
// function ProceedToPayment() {
//   const { expenseId } = useParams();
//   const [vendor, setVendor] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [emailSent, setEmailSent] = useState(false);
//   const [sentToPayment, setSentToPayment] = useState(false);
//   const [showSentBanner, setShowSentBanner] = useState(false);
 
//   // New state for sending and popup
//   const [sending, setSending] = useState(false);
//   const [showSuccess, setShowSuccess] = useState(false);
//   const [showError, setShowError] = useState(false);
 
//   const { instance, accounts } = useMsal();
 
//   useEffect(() => {
//     const fetchSelectedVendor = async () => {
//       try {
//         const res = await axios.get(
//           `http://localhost:5000/api/payments/selected-vendor/${encodeURIComponent(
//             expenseId
//           )}`
//         );
//         setVendor(res.data);
//         setSentToPayment(res.data.sent_to_payment); // Set from backend
//         setEmailSent(res.data.sent_to_payment); // Pre-fill if already sent
//         setShowSentBanner(res.data.sent_to_payment); // Show banner if already sent
//       } catch (err) {
//         console.error("Failed to fetch selected vendor", err);
//         setVendor(null);
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchSelectedVendor();
//   }, [expenseId]);
 
//   const handleSendEmail = async () => {
//     setSending(true);
//     setShowError(false);
//     setShowSentBanner(false); // Hide banner until popup is gone
//     try {
//       const account = accounts[0];
//       const result = await instance.acquireTokenSilent({
//         ...loginRequest,
//         account: account,
//       });
 
//       const accessToken = result.accessToken;
 
//       // Only POST, no PATCH needed
//       await axios.post("http://localhost:5000/api/send-payment-email", {
//         expenseId,
//         vendor,
//         accessToken,
//       });
 
//       setEmailSent(true);
//       setSentToPayment(true);
//       setShowSuccess(true);
//     } catch (err) {
//       setShowError(true);
//       console.error("Failed to send email", err);
//     } finally {
//       setSending(false);
//     }
//   };
 
//   // Close popup after 2 seconds, then show banner
//   useEffect(() => {
//     if (showSuccess || showError) {
//       const timer = setTimeout(() => {
//         setShowSuccess(false);
//         setShowError(false);
//         if (showSuccess) setShowSentBanner(true);
//       }, 2000);
//       return () => clearTimeout(timer);
//     }
//   }, [showSuccess, showError]);
 
//   if (loading) return <div className="container mt-4">Loading...</div>;
//   if (!vendor)
//     return (
//       <div className="container mt-4">
//         No selected vendor found for this expense.
//       </div>
//     );
 
//   return (
//     <div className="container mt-4">
//       <h3>Proceed to Payment</h3>
//       <p>
//         <strong>Expense ID:</strong> {expenseId}
//       </p>
//       <table className="table table-bordered">
//         <tbody>
//           <tr>
//             <th>Vendor Name</th>
//             <td>{vendor.vendor_name}</td>
//           </tr>
//           <tr>
//             <th>Cost</th>
//             <td>{vendor.cost}</td>
//           </tr>
//           <tr>
//             <th>Date</th>
//             <td>{vendor.date}</td>
//           </tr>
//           <tr>
//             <th>Payment Terms</th>
//             <td>{vendor.payment_terms}</td>
//           </tr>
//           <tr>
//             <th>Description</th>
//             <td>{vendor.description}</td>
//           </tr>
//         </tbody>
//       </table>
//       {!sentToPayment ? (
//         <button
//           className="btn btn-success"
//           onClick={handleSendEmail}
//           disabled={sending}
//         >
//           {sending ? (
//             <>
//               <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
//               {" Sending..."}
//             </>
//           ) : (
//             "Send to Payments Team"
//           )}
//         </button>
//       ) : (
//         showSentBanner && (
//           <div className="alert alert-success mt-3">
//             Email already sent to payments team!
//           </div>
//         )
//       )}
 
//       {/* Success popup */}
//       {showSuccess && (
//         <div
//           className="alert alert-success"
//           style={{
//             position: "fixed",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             zIndex: 9999,
//             minWidth: 300,
//             textAlign: "center"
//           }}
//         >
//           Email sent successfully!
//         </div>
//       )}
//       {/* Error popup */}
//       {showError && (
//         <div
//           className="alert alert-danger"
//           style={{
//             position: "fixed",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             zIndex: 9999,
//             minWidth: 300,
//             textAlign: "center"
//           }}
//         >
//           Failed to send email. Please try again.
//         </div>
//       )}
//     </div>
//   );
// }
 
// export default ProceedToPayment;